import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  where,
  getDocs,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { ShoppingList, ShoppingListItem } from '../types/shoppingList';

const COLLECTION_NAME = 'shoppingLists';

export const createShoppingList = async (
  userId: string,
  title: string,
  eventId?: string
): Promise<string> => {
  try {
    const newList: Omit<ShoppingList, 'id'> = {
      userId,
      title,
      items: [],
      createdAt: new Date(),
      updatedAt: new Date(),
      status: 'active',
      ...(eventId && { eventId }),
    };

    const docRef = await addDoc(collection(db, COLLECTION_NAME), {
      ...newList,
      createdAt: Timestamp.fromDate(newList.createdAt),
      updatedAt: Timestamp.fromDate(newList.updatedAt),
    });

    return docRef.id;
  } catch (error) {
    console.error('Erro ao criar lista de compras:', error);
    throw error;
  }
};

export const getUserShoppingLists = async (userId: string): Promise<ShoppingList[]> => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      where('userId', '==', userId),
      orderBy('updatedAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
      } as ShoppingList;
    });
  } catch (error) {
    console.error('Erro ao buscar listas de compras:', error);
    throw error;
  }
};

export const getActiveShoppingLists = async (userId: string): Promise<ShoppingList[]> => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      where('userId', '==', userId),
      where('status', '==', 'active'),
      orderBy('updatedAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
      } as ShoppingList;
    });
  } catch (error) {
    console.error('Erro ao buscar listas ativas:', error);
    throw error;
  }
};

export const addItemToList = async (
  listId: string,
  item: Omit<ShoppingListItem, 'id'>
): Promise<void> => {
  try {
    const listRef = doc(db, COLLECTION_NAME, listId);
    const listDoc = await getDocs(query(collection(db, COLLECTION_NAME), where('__name__', '==', listId)));

    if (listDoc.empty) throw new Error('Lista não encontrada');

    const currentItems = listDoc.docs[0].data().items || [];
    const newItem: ShoppingListItem = {
      ...item,
      id: `item_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    };

    await updateDoc(listRef, {
      items: [...currentItems, newItem],
      updatedAt: Timestamp.now(),
    });
  } catch (error) {
    console.error('Erro ao adicionar item:', error);
    throw error;
  }
};

export const updateItem = async (
  listId: string,
  itemId: string,
  updates: Partial<ShoppingListItem>
): Promise<void> => {
  try {
    const listRef = doc(db, COLLECTION_NAME, listId);
    const listDoc = await getDocs(query(collection(db, COLLECTION_NAME), where('__name__', '==', listId)));

    if (listDoc.empty) throw new Error('Lista não encontrada');

    const currentItems: ShoppingListItem[] = listDoc.docs[0].data().items || [];
    const updatedItems = currentItems.map((item) =>
      item.id === itemId ? { ...item, ...updates } : item
    );

    await updateDoc(listRef, {
      items: updatedItems,
      updatedAt: Timestamp.now(),
    });
  } catch (error) {
    console.error('Erro ao atualizar item:', error);
    throw error;
  }
};

export const removeItemFromList = async (listId: string, itemId: string): Promise<void> => {
  try {
    const listRef = doc(db, COLLECTION_NAME, listId);
    const listDoc = await getDocs(query(collection(db, COLLECTION_NAME), where('__name__', '==', listId)));

    if (listDoc.empty) throw new Error('Lista não encontrada');

    const currentItems: ShoppingListItem[] = listDoc.docs[0].data().items || [];
    const updatedItems = currentItems.filter((item) => item.id !== itemId);

    await updateDoc(listRef, {
      items: updatedItems,
      updatedAt: Timestamp.now(),
    });
  } catch (error) {
    console.error('Erro ao remover item:', error);
    throw error;
  }
};

export const updateShoppingList = async (
  listId: string,
  updates: Partial<ShoppingList>
): Promise<void> => {
  try {
    const listRef = doc(db, COLLECTION_NAME, listId);
    await updateDoc(listRef, {
      ...updates,
      updatedAt: Timestamp.now(),
    });
  } catch (error) {
    console.error('Erro ao atualizar lista:', error);
    throw error;
  }
};

export const deleteShoppingList = async (listId: string): Promise<void> => {
  try {
    await deleteDoc(doc(db, COLLECTION_NAME, listId));
  } catch (error) {
    console.error('Erro ao deletar lista:', error);
    throw error;
  }
};

export const toggleItemChecked = async (
  listId: string,
  itemId: string
): Promise<void> => {
  try {
    const listRef = doc(db, COLLECTION_NAME, listId);
    const listDoc = await getDocs(query(collection(db, COLLECTION_NAME), where('__name__', '==', listId)));

    if (listDoc.empty) throw new Error('Lista não encontrada');

    const currentItems: ShoppingListItem[] = listDoc.docs[0].data().items || [];
    const updatedItems = currentItems.map((item) =>
      item.id === itemId ? { ...item, checked: !item.checked } : item
    );

    await updateDoc(listRef, {
      items: updatedItems,
      updatedAt: Timestamp.now(),
    });
  } catch (error) {
    console.error('Erro ao marcar item:', error);
    throw error;
  }
};
